import React, { useCallback, useEffect, useRef, useState } from 'react';
import { TextInput, View, Text, Platform } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';

// Preset amount chips temporarily removed — restore along with the
// Pressable/SolidButton imports and the chips row below.
// const QUICK_AMOUNTS = [25, 50, 100, 500];
// const HERO_QUICK_AMOUNTS = [10, 25, 50, 100];

const centsToDollars = (cents) => (cents / 100).toFixed(2);
const formatCurrency = (text) => {
    const cleaned = String(text ?? '').replace(/[^0-9.]/g, '');
    const num = parseFloat(cleaned);
    if (Number.isNaN(num)) return '';
    return num.toFixed(2);
};
const parseCents = (text) => {
    const cleaned = String(text ?? '').replace(/[^0-9.]/g, '');
    if (cleaned === '') return 0;
    const dollars = parseFloat(cleaned);
    if (Number.isNaN(dollars) || dollars < 0) return 0;
    return Math.round(dollars * 100);
};

export default function AmountInput({ amountCents = 0, onChange, testID = 'amount-input', variant = 'default', heroTextColor = null }) {
    const [text, setText] = useState(amountCents > 0 ? centsToDollars(amountCents) : '');
    const [isFocused, setIsFocused] = useState(false);
    const inputRef = useRef(null);
    const isHero = variant === 'hero';
    // const quickAmounts = isHero ? HERO_QUICK_AMOUNTS : QUICK_AMOUNTS;

    // Sync from external changes (e.g. quick amount chips, reset) when not focused
    useEffect(() => {
        if (!isFocused) {
            setText(amountCents > 0 ? centsToDollars(amountCents) : '');
        }
    }, [amountCents, isFocused]);

    const handleChangeText = useCallback((newText) => {
        // Allow free typing — only strip non-numeric chars, keep the rest as-is
        const cleaned = newText.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');
        setText(cleaned);
        const cents = parseCents(cleaned);
        onChange?.(cents);
    }, [onChange]);

    const handleBlur = useCallback(() => {
        setIsFocused(false);
        // Format on blur
        const formatted = formatCurrency(text);
        setText(formatted);
        const cents = parseCents(formatted);
        onChange?.(cents);
    }, [text, onChange]);

    const handleFocus = useCallback(() => {
        setIsFocused(true);
        // Select all text on focus for easy editing
    }, []);

    const displayValue = isFocused ? text : (amountCents > 0 ? `$${centsToDollars(amountCents)}` : '');

    // const chipLabel = (dollars) => `$${dollars}`;
    // const chipPress = (dollars) => {
    //     setText(centsToDollars(dollars * 100));
    //     onChange?.(dollars * 100);
    // };

    return (
        <View style={styles.container} testID={testID}>
            <TextInput
                ref={inputRef}
                testID={`${testID}-field`}
                style={[styles.input, isHero && styles.heroInput, isHero && heroTextColor && { color: heroTextColor }]}
                value={displayValue}
                onChangeText={handleChangeText}
                onBlur={handleBlur}
                onFocus={handleFocus}
                keyboardType="decimal-pad"
                selectTextOnFocus
                autoFocus
                accessibilityLabel="Gift amount"
                placeholder="$0.00"
                placeholderTextColor={isHero && heroTextColor ? `${heroTextColor}80` : (isHero ? styles.heroPlaceholder.color : styles.placeholder.color)}
            />
            {/* <View style={styles.chipsRow}>
                {quickAmounts.map((dollars) => {
                    const isSelected = amountCents === dollars * 100;
                    if (isHero) {
                        return (
                            <Pressable
                                key={dollars}
                                testID={`${testID}-chip-${dollars}`}
                                onPress={() => chipPress(dollars)}
                                style={[
                                    styles.heroChip,
                                    heroTextColor && { backgroundColor: `${heroTextColor}1F` },
                                    isSelected && styles.heroChipSelected,
                                    isSelected && heroTextColor && { backgroundColor: heroTextColor },
                                ]}
                            >
                                <Text style={[
                                    styles.heroChipText,
                                    heroTextColor && { color: heroTextColor },
                                    isSelected && styles.heroChipTextSelected,
                                    isSelected && baseColor && { color: baseColor },
                                ]}>
                                    {chipLabel(dollars)}
                                </Text>
                            </Pressable>
                        );
                    }
                    return (
                        <SolidButton
                            key={dollars}
                            testID={`${testID}-chip-${dollars}`}
                            text={chipLabel(dollars)}
                            size="sm"
                            variant={isSelected ? 'normal' : 'outlined'}
                            type="giving"
                            onPress={() => chipPress(dollars)}
                        />
                    );
                })}
            </View> */}
            <Text
                style={[styles.hint, isHero && styles.heroHint, isHero && heroTextColor && { color: `${heroTextColor}B3` }]}
                testID={`${testID}-hint`}
            >
                {isHero
                    ? (Platform.OS === 'web' ? 'Click the amount to edit it.' : 'Tap the amount to edit it.')
                    : 'Enter the total amount for your gift.'}
            </Text>
        </View>
    );
}

const styles = StyleSheet.create(({ colors, fonts, spacing, roundness, typography }) => ({
    container: {
        alignItems: 'center',
        gap: spacing.md,
    },
    input: {
        width: '100%',
        textAlign: 'center',
        fontSize: 56,
        fontFamily: fonts.heading,
        color: colors.text,
        paddingVertical: spacing.md,
        // No default focus ring — the hero amount is the page centerpiece.
        outlineStyle: 'none',
    },
    heroInput: {
        color: colors.onGivingHero,
        fontSize: 48,
        paddingVertical: spacing.sm,
    },
    placeholder: {
        color: colors.textTertiary,
    },
    heroPlaceholder: {
        color: `${colors.onGivingHero}80`,
    },
    chipsRow: {
        flexDirection: 'row',
        gap: spacing.sm,
        justifyContent: 'center',
        flexWrap: 'wrap',
    },
    heroChip: {
        paddingVertical: spacing.xs,
        paddingHorizontal: spacing.md,
        borderRadius: roundness.xxl,
        backgroundColor: `${colors.onGivingHero}1F`,
    },
    heroChipSelected: {
        backgroundColor: colors.onGivingHero,
    },
    heroChipText: {
        fontSize: typography.body,
        fontFamily: fonts.bold,
        color: colors.onGivingHero,
    },
    heroChipTextSelected: {
        color: colors.givingHeroStart,
    },
    hint: {
        fontSize: typography.caption,
        fontFamily: fonts.body,
        color: colors.textSecondary,
    },
    heroHint: {
        color: `${colors.onGivingHero}B3`,
    },
}));
